"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { downloadText } from "@/lib/tool-kit/download";
import {
  cleanTranscript,
  DEFAULT_TRANSCRIPT_OPTIONS,
  type FillerLevel,
  type SpeakerStyle,
  type TimeStyle,
} from "@/lib/tool-kit/qualitative-text/transcript-clean";
import {
  TRANSCRIPT_FORMATS,
  type TranscriptFormat,
} from "@/lib/tool-kit/qualitative-text/transcript-parse";

/** English strings. A translated content file overrides these through `ui`. */
export const TRANSCRIPT_CLEANER_STRINGS = {
  privacy:
    "Runs entirely in your browser. Your transcript and any file you open are never uploaded.",
  inputLabel: "Transcript to clean",
  inputPlaceholder:
    "Paste a YouTube transcript, a Zoom or Teams transcript, or the contents of a .vtt or .srt file.",
  fileLabel: "Or open a .vtt, .srt or .txt file",
  sampleYoutube: "Sample: YouTube paste",
  sampleTeams: "Sample: Teams .vtt",
  clear: "Clear",
  formatLabel: "Input format",
  formatAuto: "Detect automatically",
  formats: {
    vtt: "WebVTT (.vtt)",
    srt: "SubRip (.srt)",
    zoom: "Zoom text, [Name] 00:00:05",
    stamped: "Timestamps on their own lines (YouTube)",
    plain: "Plain text",
  } as Record<TranscriptFormat, string>,
  speakersLabel: "Speaker names",
  speakers: {
    labels: "Keep, as Name: text",
    markdown: "Keep, in bold (Markdown)",
    none: "Remove",
  } as Record<SpeakerStyle, string>,
  timestampsLabel: "Timestamps",
  timestamps: {
    none: "Remove",
    turn: "Keep one at the start of each turn",
  } as Record<TimeStyle, string>,
  fillersLabel: "Filler words",
  fillers: {
    none: "Keep everything (verbatim)",
    disfluencies: "Remove um, uh, er, erm, hmm, mm",
    intelligent: "Also remove “you know” and “I mean” set off by commas",
  } as Record<FillerLevel, string>,
  mergeTurns: "Merge a speaker's consecutive captions into one turn",
  repeats: "Collapse immediate repeats (I I think becomes I think)",
  soundCues: "Remove [Music], [Applause] and ♪ lines",
  paragraphLabel: "New paragraph after this many sentences (0 for never)",
  customLabel: "Other words or phrases to remove",
  customHint:
    "Separate with commas or line breaks. Whole words only, any capitalization.",
  emptyPrompt: "Paste a transcript or open a file to clean it.",
  results: "Cleaned transcript",
  detected: "Layout read as: {format}",
  statCaptions: "Captions read",
  statTurns: "Turns",
  statWords: "Words, before → after",
  wordsFormat: "{before} → {after}",
  statRemoved: "Removals",
  outputLabel: "Cleaned transcript",
  copy: "Copy text",
  copied: "Copied!",
  download: "Download .txt",
  filename: "cleaned-transcript.txt",
  speakersHeading: "Who said how much",
  colSpeaker: "Speaker",
  colTurns: "Turns",
  colWords: "Words",
  nothingFound: "No text was found in this input.",
  sampleYoutubeText:
    "0:00\n[Music]\n0:04\nso today I want to talk about um\n0:07\nsleep and how it affects memory.\n0:12\nyou know, most students think\n0:15\nall-nighters help. They don't.",
  sampleTeamsText:
    "WEBVTT\n\n0001/12-0\n00:00:05.000 --> 00:00:09.500\n<v Priya Nair>So, um, how did you first get into nursing?</v>\n\n0002/13-0\n00:00:10.000 --> 00:00:14.000\n<v Tom Hale>I I started on the wards in 2019,</v>\n\n0003/13-1\n00:00:14.000 --> 00:00:17.000\n<v Tom Hale>uh, after my degree.</v>",
} as const;

const FORMAT_OPTION = "auto";

export default function TranscriptCleanerWidget() {
  const strings = useToolUi(TRANSCRIPT_CLEANER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [format, setFormat] = useState<string>(FORMAT_OPTION);
  const [speakers, setSpeakers] = useState<SpeakerStyle>("labels");
  const [timestamps, setTimestamps] = useState<TimeStyle>("none");
  const [fillers, setFillers] = useState<FillerLevel>("none");
  const [mergeTurns, setMergeTurns] = useState(true);
  const [repeats, setRepeats] = useState(false);
  const [soundCues, setSoundCues] = useState(true);
  const [paragraph, setParagraph] = useState("0");
  const [custom, setCustom] = useState("");
  const deferred = useDeferredValue(text);

  const result = useMemo(
    () =>
      deferred.trim()
        ? cleanTranscript(deferred, {
            ...DEFAULT_TRANSCRIPT_OPTIONS,
            format:
              format === FORMAT_OPTION ? null : (format as TranscriptFormat),
            speakers,
            timestamps,
            fillers,
            mergeTurns,
            repeats,
            soundCues,
            paragraphSentences: Math.max(0, parseIntegerInput(paragraph) ?? 0),
            customFillers: custom,
          })
        : null,
    [
      deferred,
      format,
      speakers,
      timestamps,
      fillers,
      mergeTurns,
      repeats,
      soundCues,
      paragraph,
      custom,
    ]
  );

  async function openFile(file: File | undefined) {
    if (file) {
      setText(await file.text());
    }
  }

  return (
    <>
      <Notice>{strings.privacy}</Notice>
      <TextAreaField
        id="tc-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sampleYoutubeText)}
          type="button"
        >
          {strings.sampleYoutube}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sampleTeamsText)}
          type="button"
        >
          {strings.sampleTeams}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
        <label className={LABEL} htmlFor="tc-file">
          {strings.fileLabel}
        </label>
        <input
          accept=".vtt,.srt,.txt,text/plain,text/vtt"
          className="text-foreground text-sm"
          id="tc-file"
          onChange={(event) => {
            openFile(event.target.files?.[0]);
            event.target.value = "";
          }}
          type="file"
        />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField
          id="tc-format"
          label={strings.formatLabel}
          onChange={setFormat}
          options={[
            { value: FORMAT_OPTION, label: strings.formatAuto },
            ...TRANSCRIPT_FORMATS.map((value) => ({
              value: value as string,
              label: strings.formats[value],
            })),
          ]}
          value={format}
        />
        <SelectField
          id="tc-speakers"
          label={strings.speakersLabel}
          onChange={setSpeakers}
          options={(Object.keys(strings.speakers) as SpeakerStyle[]).map(
            (value) => ({ value, label: strings.speakers[value] })
          )}
          value={speakers}
        />
        <SelectField
          id="tc-timestamps"
          label={strings.timestampsLabel}
          onChange={setTimestamps}
          options={(Object.keys(strings.timestamps) as TimeStyle[]).map(
            (value) => ({ value, label: strings.timestamps[value] })
          )}
          value={timestamps}
        />
        <SelectField
          id="tc-fillers"
          label={strings.fillersLabel}
          onChange={setFillers}
          options={(Object.keys(strings.fillers) as FillerLevel[]).map(
            (value) => ({ value, label: strings.fillers[value] })
          )}
          value={fillers}
        />
        <div className="space-y-2">
          <Checkbox
            checked={mergeTurns}
            label={strings.mergeTurns}
            onChange={setMergeTurns}
          />
          <Checkbox
            checked={repeats}
            label={strings.repeats}
            onChange={setRepeats}
          />
          <Checkbox
            checked={soundCues}
            label={strings.soundCues}
            onChange={setSoundCues}
          />
        </div>
        <TextField
          id="tc-paragraph"
          label={strings.paragraphLabel}
          numeric
          onChange={setParagraph}
          value={paragraph}
        />
      </div>
      <TextAreaField
        hint={strings.customHint}
        id="tc-custom"
        label={strings.customLabel}
        onChange={setCustom}
        rows={2}
        value={custom}
      />
      {result ? (
        <section aria-live="polite" className="space-y-5">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {result.turns === 0 ? (
            <Notice>{strings.nothingFound}</Notice>
          ) : (
            <>
              <p className={HINT}>
                {t(strings.detected, {
                  format: strings.formats[result.format],
                })}
              </p>
              <StatGrid>
                <StatCard
                  label={strings.statCaptions}
                  value={fmt(result.cues)}
                />
                <StatCard label={strings.statTurns} value={fmt(result.turns)} />
                <StatCard
                  emphasis
                  label={strings.statWords}
                  value={t(strings.wordsFormat, {
                    before: fmt(result.wordsBefore),
                    after: fmt(result.wordsAfter),
                  })}
                />
                <StatCard
                  label={strings.statRemoved}
                  value={fmt(result.removed)}
                />
              </StatGrid>
              <div className="space-y-2">
                <label className={LABEL} htmlFor="tc-output">
                  {strings.outputLabel}
                </label>
                <textarea
                  className={TEXTAREA}
                  id="tc-output"
                  readOnly
                  rows={10}
                  value={result.output}
                />
                <div className="flex flex-wrap gap-3">
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copy}
                    text={result.output}
                  />
                  <button
                    className={BUTTON_SECONDARY}
                    onClick={() =>
                      downloadText(strings.filename, result.output)
                    }
                    type="button"
                  >
                    {strings.download}
                  </button>
                </div>
              </div>
              {result.speakers.length > 0 ? (
                <div className="space-y-2">
                  <h3 className={WIDGET_H3}>{strings.speakersHeading}</h3>
                  <DataTable
                    head={[
                      strings.colSpeaker,
                      strings.colTurns,
                      strings.colWords,
                    ]}
                    rows={result.speakers.map((row) => [
                      row.speaker,
                      fmt(row.turns),
                      fmt(row.words),
                    ])}
                  />
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
